import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Download, ArrowRight, Shield, Zap, Award, BookOpen, Atom, Target, Gauge } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { NavBar, Footer } from "../components/Layout";
import { trackEvent } from "../hooks/usePageTracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COVER = "https://static.prod-images.emergentagent.com/jobs/fb03f6d6-6305-409a-adb6-3fa2acdfc7db/images/a50239aac0225a7d1fb4eb87d874ba720bc5f83025f9e51719b007380bc202bc.jpeg";

const chapters = [
  { part: "1", title: "Cinématique", items: ["Mouvement rectiligne uniforme (MRU)", "Mouvement rectiligne uniformément accéléré (MRUA)", "Chute libre et projectiles", "Mouvement circulaire uniforme"] },
  { part: "2", title: "Dynamique - Lois de Newton", items: ["Première loi : inertie", "Deuxième loi : F = ma", "Troisième loi : action-réaction", "Diagrammes de corps libre", "Forces de frottement et plans inclinés"] },
  { part: "3", title: "Travail et énergie", items: ["Travail d'une force", "Énergie cinétique et potentielle", "Conservation de l'énergie", "Puissance"] },
  { part: "4", title: "Quantité de mouvement", items: ["Impulsion et quantité de mouvement", "Conservation de la quantité de mouvement", "Collisions élastiques et inélastiques"] },
];

const partColors = ["text-orange-400 bg-orange-500/20", "text-blue-400 bg-blue-500/20", "text-emerald-400 bg-emerald-500/20", "text-purple-400 bg-purple-500/20"];

const FAQ_MECANIQUE = [
  { q: "Que contient le livre Mécanique en action?", a: "Le livre contient 200+ pages couvrant tout le programme de physique mécanique 203-SN1-Re du cégep. Il inclut plus de 200 exemples et exercices résolus avec solutions détaillées sur la cinématique, la dynamique (lois de Newton), le travail et l'énergie, et la quantité de mouvement." },
  { q: "Ce livre est-il adapté au cours 203-SN1-Re?", a: "Oui, le livre est spécifiquement conçu pour le cours de physique mécanique 203-SN1-Re offert dans les cégeps du Québec. Il couvre toutes les notions exigées : cinématique, dynamique, travail, énergie et quantité de mouvement." },
  { q: "Comment recevoir le livre après l'achat?", a: "Après le paiement sécurisé par carte (29,99$ CAD), vous recevez immédiatement un lien de téléchargement en format PDF. Aucun compte nécessaire." },
  { q: "Y a-t-il un aide-mémoire gratuit en mécanique?", a: "Oui! Nous offrons un aide-mémoire gratuit de 5 pages résumant les formules essentielles de physique mécanique. Rendez-vous sur notre page de téléchargement gratuit." },
  { q: "Ce livre peut-il aider pour la préparation aux examens de physique au cégep?", a: "Absolument! Avec 200+ exercices résolus progressifs, le livre est l'outil idéal pour la préparation aux examens de physique mécanique au cégep. Les solutions détaillées vous aident à comprendre chaque étape de résolution." },
];

const MecaniquePage = () => {
  const [isLoading, setIsLoading] = useState(false);

  const handleBuyNow = async () => {
    setIsLoading(true);
    trackEvent("/mecanique", "click_buy_mecanique");
    try {
      const originUrl = window.location.origin;
      const r = await axios.post(`${API}/checkout/create-session`, { product_id: "mecanique-cegep", origin_url: originUrl });
      window.location.href = r.data.url;
    } catch {
      toast.error("Erreur lors du paiement. Réessaie.");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900">
      <NavBar active="mecanique" />

      {/* Hero */}
      <section className="relative py-16 sm:py-24 overflow-hidden gradient-hero grid-overlay">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-500/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4"></div>
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-orange-500/20">
                Nouveau
              </div>
              <div className="badge-gold mb-4">Cours 203-SN1-Re - Cégep du Québec</div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
                Mécanique{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">en action</span>
              </h1>
              <p className="text-lg text-blue-200/70 mb-4 max-w-lg">
                Le livre complet de <strong className="text-white">physique mécanique</strong> pour le cégep.
                200+ pages : résumé de cours complet, plusieurs exemples et exercices résolus pour le cours <strong className="text-white">203-SN1-Re</strong>. Conforme au programme de mécanique du cégep.
              </p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-8 max-w-lg backdrop-blur-sm">
                <p className="text-blue-100/80 text-sm">
                  <strong className="text-orange-400">Contenu complet :</strong> Résumé de cours complet avec cinématique, dynamique (lois de Newton), diagrammes de corps libre, travail et énergie, quantité de mouvement et collisions. Plusieurs exemples et exercices résolus étape par étape. Conforme au programme de mécanique du cégep.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
                <button data-testid="buy-now-hero-button" onClick={handleBuyNow} disabled={isLoading}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 text-slate-900 font-bold px-8 py-4 rounded-lg hover:from-orange-400 hover:to-amber-400 transition-all shadow-lg shadow-orange-500/20 flex items-center gap-2 text-lg">
                  {isLoading ? <div className="spinner"></div> : <>Acheter maintenant <ArrowRight className="w-5 h-5" /></>}
                </button>
                <div>
                  <span className="text-3xl font-bold text-orange-400">29,99$</span>
                  <span className="text-blue-300/50 ml-1">CAD</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2 text-sm text-blue-200/60"><CheckCircle className="w-5 h-5 text-emerald-400" /> Téléchargement immédiat</div>
                <div className="flex items-center gap-2 text-sm text-blue-200/60"><Shield className="w-5 h-5 text-blue-400" /> Paiement sécurisé</div>
                <div className="flex items-center gap-2 text-sm text-blue-200/60"><Zap className="w-5 h-5 text-orange-400" /> PDF 200+ pages</div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="flex justify-center">
              <div className="relative max-w-sm book-mockup">
                <motion.div animate={{ y: [-5, 5, -5] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-6 -right-6 w-14 h-14 bg-orange-500/60 rounded-xl rotate-12 shadow-lg shadow-orange-500/20 z-10" />
                <motion.div animate={{ y: [5, -5, 5] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -bottom-4 -left-4 w-10 h-10 bg-blue-500/60 rounded-full shadow-lg shadow-blue-500/20 z-10" />
                <div className="book-mockup-inner">
                  <div className="relative aspect-[1/1.5] rounded-2xl shadow-2xl overflow-hidden glow-gold border border-white/10">
                    <img src={COVER} alt="Livre Mécanique en action - Exercices résolus physique 203-SN1-Re cégep Québec" className="w-full h-full object-cover" loading="eager" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-navy-800 border-y border-white/5">
        <div className="max-w-6xl mx-auto px-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: BookOpen, title: "200+ pages", desc: "Contenu complet" },
            { icon: Target, title: "200+ exercices", desc: "Tous résolus" },
            { icon: Atom, title: "Cours 203-SN1-Re", desc: "Programme cégep" },
            { icon: Award, title: "Cégep du Québec", desc: "Conforme au programme" },
          ].map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="text-center p-6 bg-white/5 rounded-2xl border border-white/5">
              <f.icon className="w-8 h-8 text-orange-400 mx-auto mb-3" />
              <h3 className="font-semibold text-white mb-1">{f.title}</h3>
              <p className="text-blue-200/60 text-sm">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-navy-900">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-white text-center mb-12">Programme complet : physique mécanique 203-SN1-Re</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {chapters.map((ch, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="card-dark hover:border-white/20 transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 ${partColors[i]} rounded-lg flex items-center justify-center`}>
                    <span className="font-bold">{ch.part}</span>
                  </div>
                  <h3 className="font-semibold text-xl text-white">{ch.title}</h3>
                </div>
                <ul className="space-y-3">
                  {ch.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-blue-200/70">
                      <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-navy-800" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-display font-bold text-3xl text-white text-center mb-12">Questions fréquentes sur la physique mécanique au cégep</h2>
          <div className="space-y-4">
            {FAQ_MECANIQUE.map((faq, i) => (
              <details key={i} className="card-dark group" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <summary className="cursor-pointer font-semibold text-white flex items-center justify-between" itemProp="name">
                  {faq.q}
                  <span className="text-orange-400 group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <div className="mt-4 text-blue-200/70 leading-relaxed" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p itemProp="text">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-sell */}
      <section className="py-16 bg-navy-900 border-y border-white/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display font-bold text-2xl text-white mb-6">Autres ressources pédagogiques</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/sn4" className="card-dark inline-flex items-center gap-3 hover:border-amber-500/30 transition-all">
              <div className="text-left"><p className="font-semibold text-white">Maths SN4</p><p className="text-amber-400 font-bold">26,99$</p></div>
            </Link>
            <Link to="/cst4" className="card-dark inline-flex items-center gap-3 hover:border-amber-500/30 transition-all">
              <div className="text-left"><p className="font-semibold text-white">Maths CST4</p><p className="text-amber-400 font-bold">24,99$</p></div>
            </Link>
            <Link to="/gratuit" className="card-dark inline-flex items-center gap-3 hover:border-emerald-500/30 transition-all">
              <div className="text-left"><p className="font-semibold text-white">Aide-mémoires gratuits</p><p className="text-emerald-400 font-bold">Téléchargement gratuit</p></div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mb-6">Prêt pour ton examen de physique mécanique?</h2>
          <p className="text-lg text-slate-800 mb-8">200+ exercices résolus pour le cours 203-SN1-Re. Téléchargement PDF immédiat.</p>
          <button data-testid="buy-now-cta-button" onClick={handleBuyNow} disabled={isLoading}
            className="bg-slate-900 text-white font-bold px-10 py-4 rounded-lg hover:bg-slate-800 transition-all shadow-xl flex items-center gap-3 mx-auto text-lg">
            {isLoading ? <div className="spinner"></div> : <><Download className="w-6 h-6" /> Acheter - 29,99$ CAD</>}
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MecaniquePage;
